import { type FC, useCallback, useMemo, useState } from "react";

import { Checkbox, Popover, PopoverPlacementTypes, SearchBar } from "@snapchat/snap-design-system";
import { ChevronDownIcon, FilterFilledIcon } from "@snapchat/snap-design-system-icons";
import { useIntl } from "react-intl";

import { Flexbox } from "../../../shared/components";
import { getKeys } from "../../../shared/utils/object";

import {
    divider,
    filterText,
    linkButton,
    overlayContainer,
    overlayItem,
    overlayTrigger,
    searchBar,
} from "./MetricFilter.styles";
import { type EventInsightMetric, type Metrics, defaultMetrics } from "./constants";
import { DeselectAll, Filter, SelectAll, messages } from "./messages.generated";

interface MetricFilter {
    metrics: Metrics;
    onFilterChange: (activeMetrics: Metrics) => void;
}

export const MetricFilter: FC<MetricFilter> = ({ metrics, onFilterChange }) => {
    const intl = useIntl();

    const [searchText, setSearchText] = useState("");

    const handleToggleMetric = useCallback(
        (metric: EventInsightMetric) => {
            onFilterChange({
                ...metrics,
                [metric]: { ...metrics[metric], isActive: !metrics[metric].isActive },
            });
        },
        [metrics, onFilterChange],
    );

    const handleSelectAll = useCallback(() => {
        onFilterChange({ ...defaultMetrics });
    }, [onFilterChange]);

    const handleDeselectAll = useCallback(() => {
        const values = { ...defaultMetrics };
        for (const key in values) {
            values[key as keyof typeof values] = { ...values[key as keyof typeof values], isActive: false };
        }
        onFilterChange(values);
    }, [onFilterChange]);

    const shownMetrics = useMemo(() => {
        return getKeys(defaultMetrics).filter((metric: EventInsightMetric) =>
            // TODO - https://en.wikipedia.org/wiki/Levenshtein_distance
            intl
                .formatMessage(defaultMetrics[metric].label)
                .trim()
                .toLocaleUpperCase()
                .includes(searchText.trim().toLocaleUpperCase()),
        );
    }, [intl, searchText]);

    const overlay = useMemo(() => {
        return (
            <>
                <SearchBar
                    className={searchBar}
                    placeholder={intl.formatMessage(messages.filterSearchPlaceholder)}
                    value={searchText}
                    onChange={setSearchText}
                />
                {shownMetrics.map((metric: EventInsightMetric) => (
                    <Checkbox
                        containerClassName={overlayItem}
                        key={metric}
                        label={intl.formatMessage(defaultMetrics[metric].label)}
                        value={metrics[metric].isActive}
                        onChange={() => {
                            handleToggleMetric(metric);
                        }}
                    />
                ))}
                <hr className={divider} />
                <Flexbox justifyContent="space-between">
                    <button className={linkButton} onClick={handleSelectAll}>
                        <SelectAll />
                    </button>
                    <button className={linkButton} onClick={handleDeselectAll}>
                        <DeselectAll />
                    </button>
                </Flexbox>
            </>
        );
    }, [intl, searchText, shownMetrics, handleSelectAll, handleDeselectAll, metrics, handleToggleMetric]);

    return (
        <Popover
            className={overlayContainer}
            content={overlay}
            placement={PopoverPlacementTypes.BOTTOM_RIGHT}
            trigger="click"
        >
            <div className={overlayTrigger}>
                <FilterFilledIcon />
                <span className={filterText}>
                    <Filter />
                </span>
                <ChevronDownIcon />
            </div>
        </Popover>
    );
};
